// @vendors
const Immutable = require('immutable');
const { get } = require('lodash');

// @ constants
const { CM_SYNC } = require('../../constants/actionTypes');
const actionTypes = require('../../constants/actionTypes');
const {
    FIRST_STEP,
    SECOND_STEP,
    WIZARD_FIRST_STEP,
    WIZARD_SECOND_STEP,
} = require('constants/index');
const {
    CM_ACTIVE_FUND_TYPE,
    CM_PASIVE_FUND_TYPE,
    CM_PUBLIC_ACTIVE_FUND_TYPE,
    CM_ACTIVE_FUND_TYPE_TAG,
    CM_PASIVE_FUND_TYPE_TAG,
    CM_DIY_TAG,
} = require('../../constants/constants');

// @ reducers
const cmWalletBuyStep1 = require('./cmWalletBuy/cmWalletBuyStep1Legacy');
const cmWalletBuyStep2 = require('./cmWalletBuy/cmWalletBuyStep2Legacy');

const initialState = () => {
    return Immutable.Map().merge({
        simulation: {
            id: '',
            type: '',
            publicAmount: 0,
            amount: 0,
            returnPath: '',
            isDiy: false,
        },
        steps: [
            cmWalletBuyStep1(undefined, { type: null }),
            cmWalletBuyStep2(undefined, { type: null }),
        ],
        walletRisk: {
            mostRestrictiveIsin: '',
            riskPortfolioCategorization: '',
            riskPortfolioMiFid2: '',
        },
        errorReceived: '',
        isFetching: false,
        inProgress: false,
        willLeave: false,
        visibleStep: WIZARD_FIRST_STEP,
        submitSuccessful: false,
        isFetchingPortfolioInfo: false,
        isFetchingRiskCategorizationPortfolio: false,
        isFetchingFundsMinAmounts: false,
        isPortfolioInfoFetched: false,
        isRiskPortfolioCategorizationFetched: false,
        isFundsMinAmountsFetched: false,
        isSynchronized: false,
        isMinFundAmountValid: false,
        portfolios: [],
        orderValidation: {},
        minAmounts: [],
        minAmountsCurrency: '',
        error: '',
        eventTag: '',
    });
};

function updateStep(index, steps, action) {
    return steps.update(index, step => {
        switch (index) {
            case FIRST_STEP:
                return cmWalletBuyStep1(step, action);
            case SECOND_STEP:
                return cmWalletBuyStep2(step, action);
        }
    });
}

function formatFundType(fundType) {
    return fundType === CM_PUBLIC_ACTIVE_FUND_TYPE
        ? CM_ACTIVE_FUND_TYPE
        : CM_PASIVE_FUND_TYPE;
}

function filterPortfoliosByType(portfolios, type) {
    get(portfolios, '[0].assetClassInfo', []).forEach(aci => {
        get(aci, 'subAssetClassInfo', []).forEach(saci => {
            const filtered = get(saci, 'subAssetClassIsinInfo', []).filter(
                sacii => get(sacii, 'subAssetClassIndIsin') === type,
            );
            saci.subAssetClassIsinInfo = filtered;
        });
    });

    return portfolios;
}

function formatConfimation(funds) {
    const assetsById = {};
    funds.forEach(fund => {
        if (assetsById[fund.assetClassId]) {
            return;
        }

        assetsById[fund.assetClassId] = {
            assetClassDesc: fund.assetClassDesc,
            assetClassId: fund.assetClassId,
            subAssetClassInfo: [],
        };
    });

    funds.forEach(fund => {
        assetsById[fund.assetClassId].subAssetClassInfo.push({
            subAssetClassDesc: fund.subAssetClassDesc,
            subAssetClassId: fund.subAssetClassId,
            subAssetClassIsinInfo: [
                {
                    subAssetClassFundName: fund.subAssetClassFundsName,
                    subAssetClassDesc: fund.subAssetClassDesc,
                    subAssetClassFundAmount: fund.subAssetClassFundAmount,
                    currency: fund.currency,
                    subAssetClassIsin: fund.subAssetClassIsin,
                },
            ],
        });
    });

    return {
        assetClassInfo: Object.values(assetsById),
    };
}

function validateMinFundsAmount(funds, minAmounts, isFundsMinAmountsFetched) {
    let isValid = true;

    if (!isFundsMinAmountsFetched) {
        return { isValid: false, portfolio: funds };
    }

    if (minAmounts && minAmounts.length > 0) {
        funds.assetClassInfo.forEach(asset => {
            const subClasses = asset.subAssetClassInfo;
            subClasses.forEach(subClass => {
                const subAssets = subClass.subAssetClassIsinInfo;
                subAssets.forEach(subAsset => {
                    const fundMinAmount = minAmounts.find(
                        x => x.isin == subAsset.subAssetClassIsin,
                    );
                    if (fundMinAmount) {
                        const isFundValid =
                            subAsset.subAssetClassFundAmount >=
                            fundMinAmount.minimumAmount;
                        subAsset.isValidAmount = isFundValid;
                        isValid = isValid && isFundValid;
                        subAsset.fundMinAmount = fundMinAmount.minimumAmount;
                    }
                });
            });
        });
    }

    return { isValid, portfolio: funds };
}

function getEventTag(filteredPortfolios, type, isDiy) {
    const portfolioName = isDiy
        ? CM_DIY_TAG
        : filteredPortfolios[0].portfolioDesc;
    const typeTag =
        type === CM_ACTIVE_FUND_TYPE
            ? CM_ACTIVE_FUND_TYPE_TAG
            : CM_PASIVE_FUND_TYPE_TAG;
    return ` ${portfolioName.toLowerCase()}-${typeTag}`;
}

const cmLegacyReducer = (state = initialState(), action) => {
    let visibleStep, partialSteps;
    switch (action.type) {
        case CM_SYNC:
            return state.merge({
                simulation: {
                    id: action.payload.id,
                    type: formatFundType(action.payload.type),
                    publicAmount: action.payload.amount,
                    amount: action.payload.amount,
                    returnPath: action.payload.returnUrl,
                    isDiy: action.payload.isDiy,
                },
                isSynchronized: true,
            });
        case actionTypes.CM_SET_VISIBLE_STEP:
            return state.merge({
                visibleStep: action.payload.step,
            });
        case actionTypes.CM_WILL_CANCEL:
        case actionTypes.CM_WILL_LEAVE:
            return state.merge({
                willLeave: true,
            });
        case actionTypes.CM_VALIDATE_STEP:
            switch (state.get('visibleStep')) {
                case WIZARD_FIRST_STEP:
                    const portfolio = state.getIn(['portfolios', 0]);
                    const validMinFundsAmount = validateMinFundsAmount(
                        portfolio.toJS(),
                        state.get('minAmounts').toJS(),
                        state.get('isFundsMinAmountsFetched'),
                    );
                    partialSteps = updateStep(
                        FIRST_STEP,
                        state.get('steps'),
                        action,
                    );
                    visibleStep =
                        partialSteps.getIn([FIRST_STEP, 'valid']) &&
                        validMinFundsAmount &&
                        partialSteps.getIn([FIRST_STEP, 'validFromAPI'])
                            ? WIZARD_SECOND_STEP
                            : WIZARD_FIRST_STEP;

                    return state
                        .setIn(
                            ['portfolios', 0],
                            Immutable.fromJS(validMinFundsAmount.portfolio),
                        )
                        .merge({
                            steps: partialSteps,
                            visibleStep,
                            isMinFundAmountValid: validMinFundsAmount.isValid,
                        });
                case WIZARD_SECOND_STEP:
                    partialSteps = updateStep(
                        SECOND_STEP,
                        state.get('steps'),
                        action,
                    );
                    return state.merge({
                        steps: partialSteps,
                    });
                default:
                    return state;
            }
        case actionTypes.CM_GET_INTERVENERS_SUCCESS:
        case actionTypes.CM_RESTORE_DEFAULT_INTERVENERS:
        case actionTypes.CM_SET_ACCOUNT_INTERVENER_NAME:
        case actionTypes.CM_SET_ACCOUNT_INTERVENER_TYPE:
        case actionTypes.CM_SET_ACCOUNT:
        case actionTypes.CM_SET_AMOUNT:
        case actionTypes.CM_SET_INITIAL_AMOUNT:
            partialSteps = updateStep(FIRST_STEP, state.get('steps'), action);
            return state.merge({
                steps: partialSteps,
            });
        case actionTypes.CM_GET_INTERVENERS_REQUEST:
            return state.merge({
                isFetching: true,
            });
        case actionTypes.CM_GET_INTERVENERS_ERROR:
            return state.merge({
                isFetching: false,
            });
        case actionTypes.CM_LEGAL_DISCLAIMER_CHANGED:
            partialSteps = updateStep(SECOND_STEP, state.get('steps'), action);
            return state.merge({
                steps: partialSteps,
            });
        case actionTypes.FETCH_CM_PORTFOLIO_INFO_REQUEST:
            return state.merge({
                isFetchingPortfolioInfo: true,
            });
        case actionTypes.FETCH_CM_FUNDS_MIN_AMOUNTS_REQUEST:
            return state.merge({
                isFetchingFundsMinAmounts: true,
            });
        case actionTypes.FETCH_CM_PORTFOLIO_RISK_CATEGORIZATION_INFO_REQUEST:
            return state.set('isFetchingRiskCategorizationPortfolio', true);
        case actionTypes.FETCH_CM_FUNDS_MIN_AMOUNTS_SUCCESS:
            const initialPortfolio = state.getIn(['portfolios', 0]);
            const validatedMinAmountsPortfolio = validateMinFundsAmount(
                initialPortfolio.toJS(),
                action.payload.data.response,
                true,
            );

            return state
                .setIn(
                    ['portfolios', 0],
                    Immutable.fromJS(validatedMinAmountsPortfolio.portfolio),
                )
                .merge({
                    minAmounts: action.payload.data.response,
                    isFetchingFundsMinAmounts: false,
                    minAmountsCurrency: action.payload.data.currency,
                    isFundsMinAmountsFetched: true,
                    isMinFundAmountValid: validatedMinAmountsPortfolio.isValid,
                });
        case actionTypes.FETCH_CM_PORTFOLIO_INFO_SUCCESS:
            const filteredPortfolios = filterPortfoliosByType(
                action.payload.data,
                state.getIn(['simulation', 'type']),
            );
            const validatedFunds = validateMinFundsAmount(
                filteredPortfolios[0],
                state.get('minAmounts').toJS(),
                state.get('isFundsMinAmountsFetched'),
            );
            filteredPortfolios[0] = validatedFunds.portfolio;
            const eventTag = getEventTag(
                filteredPortfolios,
                state.getIn(['simulation', 'type']),
                state.getIn(['simulation', 'isDiy']),
            );
            return state.merge({
                isFetchingPortfolioInfo: false,
                isPortfolioInfoFetched: true,
                isMinFundAmountValid: validatedFunds.isValid,
                eventTag: eventTag,
                portfolios: filteredPortfolios,
            });
        case actionTypes.FETCH_CM_PORTFOLIO_INFO_FAILURE:
            return state.merge({
                isFetchingPortfolioInfo: false,
                isPortfolioInfoFetched: true,
            });
        case actionTypes.CM_VALIDATE_PORTFOLIO_ORDER_SUCCESS:
            return state.merge({
                orderValidation: action.payload.data,
            });
        case actionTypes.POST_CM_ORDER_CONFIRMATION_SUCCESS:
            const portfolio = state.getIn(['portfolios', 0]);
            const successful = formatConfimation(
                action.payload.data.confirmOrderResultOkOutput,
                portfolio,
            );
            const notSuccessful = formatConfimation(
                action.payload.data.confirmOrderResultKoOutput,
                portfolio,
            );
            return state.merge({
                submitSuccessful: true,
                orderConfirmationSuccessful: successful,
                orderConfirmationNotSuccessful: notSuccessful,
            });
        case actionTypes.CM_GET_PORTFOLIO_RISK_CATEGORIZATION_SUCCESS:
            return state.mergeDeep({
                walletRisk: {
                    mostRestrictiveIsin: action.payload.mostRestrictiveIsin,
                    riskPortfolioCategorization:
                        action.payload.riskPortfolioCategorization,
                    riskPortfolioMiFid2: action.payload.riskPortfolioMiFid2,
                },
                isFetchingRiskCategorizationPortfolio: false,
                isRiskPortfolioCategorizationFetched: true,
            });
        case actionTypes.CM_VALIDATE_REQUEST:
            switch (action.payload.step) {
                case WIZARD_FIRST_STEP:
                    partialSteps = updateStep(
                        FIRST_STEP,
                        state.get('steps'),
                        action,
                    );
                    return state.merge({
                        steps: partialSteps,
                    });
            }
            return state;
        case actionTypes.CM_VALIDATE_SUCCESS:
            switch (action.payload.step) {
                case WIZARD_FIRST_STEP:
                    partialSteps = updateStep(
                        FIRST_STEP,
                        state.get('steps'),
                        action,
                    );
                    return state.merge({
                        steps: partialSteps,
                        orderValidation: action.payload.data,
                    });
            }
            return state;
        case actionTypes.CM_VALIDATE_FAILURE:
            switch (action.payload.step) {
                case WIZARD_FIRST_STEP:
                    partialSteps = updateStep(
                        FIRST_STEP,
                        state.get('steps'),
                        action,
                    );
                    return state.merge({
                        steps: partialSteps,
                        error: action.payload.data.error,
                    });
            }
            return state;
        default:
            return state;
    }
};

module.exports = cmLegacyReducer;
